import React from 'react';

import { useFieldArray, UseFormReturn } from 'react-hook-form';
import { useIntl } from 'react-intl';

import SettingsContext from 'src/features/administration/context/SettingsContext';
import { MembershipTypeFormSchemaValues } from 'src/features/membershipType/formSchema';
import { MEMBERSHIP_TEMPLATE_CATEGORY } from 'src/features/membershipType/types';
import { MEMBERSHIP_TEMPLATE_TRANSLATION_MAP } from 'src/features/membershipType/utils/constants';
import { membershipTypeMessages } from 'src/features/membershipType/utils/messages';
import { languageOptions } from 'src/features/profile/utils';
import { useListSites } from 'src/features/sites/api/useListSites';
import { siteMessages } from 'src/features/sites/utils/messages';
import { useParking } from 'src/providers/ParkingProvider';
import InputFormField from 'src/registry/components/forms/InputFormField';
import SelectFormField from 'src/registry/components/forms/SelectFormField';
import TextareaFormField from 'src/registry/components/forms/TextareaFormField';
import { WizardStepInfo } from 'src/registry/ui/Wizard';
import { generalMessages } from 'src/registry/utils/messages';
import { commonMessages } from 'src/utils/messages';
import { translate } from 'src/utils/translate';

type Props = {
    form: UseFormReturn<MembershipTypeFormSchemaValues>;
};

const translationFields = [
    { key: 'name', label: commonMessages.name },
    { key: 'description', label: generalMessages.description },
] as const;

export default function BasicsStep({ form }: Props) {
    const { formatMessage, locale } = useIntl();
    const { parkingId } = useParking();
    const { fields } = useFieldArray({
        control: form.control,
        name: 'translations',
    });

    const { translations } = form.watch();
    const settingsData = React.useContext(SettingsContext);

    React.useEffect(() => {
        if (settingsData?.languages) {
            form.setValue(
                'translations',
                settingsData.languages.map((lang, index) => ({
                    language: lang,
                    name: translations[index]?.name || '',
                    description: translations[index]?.description || '',
                })),
            );
        }
    }, [settingsData, form]);

    const { data: sitesData } = useListSites({ parkingId });

    const siteOptions = React.useMemo(() => {
        if (!sitesData) return [];
        return sitesData.results.map(({ translations, id }) => ({
            label: translate(translations, locale)?.name || '',
            value: id,
        }));
    }, [locale, sitesData]);

    return (
        <div className="flex gap-4">
            <div className="grid flex-2 grid-cols-2 gap-4">
                {translationFields.map(({ key, label }) =>
                    fields.map((_, index) =>
                        key === 'name' ? (
                            <InputFormField<MembershipTypeFormSchemaValues>
                                variant="default"
                                form={form}
                                name={`translations.${index}.${key}`}
                                inlineTitle={settingsData?.languages[index].toUpperCase() || ''}
                                label={formatMessage(label)}
                                key={`${index}.${key}`}
                                tooltip={formatMessage(membershipTypeMessages.nameTooltip)}
                                description={formatMessage(membershipTypeMessages.nameDescription, {
                                    language: settingsData?.languages[index]
                                        ? formatMessage(languageOptions[settingsData?.languages[index]]).toLowerCase()
                                        : '',
                                })}
                            />
                        ) : (
                            <TextareaFormField<MembershipTypeFormSchemaValues>
                                variant="default"
                                form={form}
                                name={`translations.${index}.${key}`}
                                inlineTitle={settingsData?.languages[index].toUpperCase() || ''}
                                label={formatMessage(label)}
                                key={`${index}.${key}`}
                                description={formatMessage(membershipTypeMessages.descriptionDescription)}
                            />
                        ),
                    ),
                )}
                <SelectFormField<MembershipTypeFormSchemaValues>
                    variant="default"
                    form={form}
                    name="category"
                    label={formatMessage(commonMessages.type)}
                    options={Object.values(MEMBERSHIP_TEMPLATE_CATEGORY)}
                    optionTranslationMap={MEMBERSHIP_TEMPLATE_TRANSLATION_MAP}
                    tooltip={formatMessage(membershipTypeMessages.typeTooltip)}
                    description={formatMessage(membershipTypeMessages.typeDescription)}
                />
                <SelectFormField<MembershipTypeFormSchemaValues>
                    variant="default"
                    form={form}
                    name="site"
                    label={formatMessage(siteMessages.site)}
                    options={siteOptions}
                    tooltip={formatMessage(membershipTypeMessages.siteTooltip)}
                    description={formatMessage(membershipTypeMessages.siteDescription)}
                />
            </div>
            <div className="flex-1">
                <WizardStepInfo
                    header={formatMessage(membershipTypeMessages.basics)}
                    body={formatMessage(membershipTypeMessages.basicsBody)}
                    tip={formatMessage(membershipTypeMessages.basicsTip)}
                />
            </div>
        </div>
    );
}
